import moment from 'moment';
import {
  ArrowDownTrayIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

import api from 'api';
import { Badge } from 'lib/components/Badge';
import { ColumnButton } from 'lib/components/ColumnButton';
import { useSafeLoading } from 'lib/components/hooks/useSafeLoading';
import { dbToUICurrencyString } from 'lib/pricing/ui';
import { ColumnService } from 'lib/services/directory';
import { PublisherInvoiceView } from 'lib/types/publisherInvoices';
import { useAppDispatch } from 'redux/hooks';
import ToastActions from 'redux/toast';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { ERequestTypes } from 'lib/types/api';
import {
  getPublisherInvoiceBadgeStatus,
  getPublisherInvoiceStatusLabel
} from './publisherInvoiceTableSettingsUtils';

type PublisherInvoicesTableRowProps = {
  invoice: PublisherInvoiceView;
};

export function PublisherInvoicesTableRow({
  invoice
}: PublisherInvoicesTableRowProps) {
  const dispatch = useAppDispatch();

  const isPayable = invoice.status === 'open' && !!invoice.hostedInvoiceUrl;

  const getPublisherInvoiceZipURL = async (
    payload: ERequestTypes['payments/publisher-invoices/download']
  ): Promise<ResponseOrError<undefined>> => {
    const { response, error } = await api.safePost(
      'payments/publisher-invoices/download',
      payload
    );
    if (error || !response) {
      return wrapError(new Error(String(error)));
    }
    window.open(response.url, '_blank'); // Open the signed URL in a new tab for download
    return wrapSuccess(undefined);
  };

  const [downloading, downloadInvoiceZip] = useSafeLoading<
    ERequestTypes['payments/publisher-invoices/download'],
    string | undefined
  >({
    callback: getPublisherInvoiceZipURL,
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Failed to download publisher invoice',
      tags: { invoiceId: invoice.id }
    }
  });

  return (
    <>
      <td>{invoice.name}</td>
      <td>{moment.utc(invoice.createdAt * 1000).format('MM/DD/YYYY')}</td>
      <td>${dbToUICurrencyString(invoice.totalInCents)}</td>
      <td>
        <Badge status={getPublisherInvoiceBadgeStatus(invoice)}>
          {getPublisherInvoiceStatusLabel(invoice)}
        </Badge>
      </td>
      <td>
        {invoice.dueDate
          ? moment.utc(invoice.dueDate * 1000).format('MM/DD/YYYY')
          : '--'}
      </td>
      <td className="flex items-center pt-5 justify-around text-align">
        <ColumnButton
          tertiary
          aria-label="Pay Invoice"
          size="sm"
          disabled={!isPayable}
          onClick={e => {
            e.stopPropagation();
            if (invoice.hostedInvoiceUrl) {
              window.open(invoice.hostedInvoiceUrl, '_blank');
            }
          }}
          startIcon={
            <CurrencyDollarIcon className="w-5 py-1 text-column-grey-400" />
          }
          type="button"
        />
        <ColumnButton
          tertiary
          aria-label="Download Invoice"
          size="sm"
          loading={downloading}
          onClick={async e => {
            e.stopPropagation();
            const { error } = await downloadInvoiceZip({
              stripeInvoiceIds: [invoice.id]
            });
            if (error) {
              dispatch(
                ToastActions.toastError({
                  headerText: 'Failed to download invoice',
                  bodyText:
                    'Our team has been notified and we will look into the issue immediately. Please try again later.'
                })
              );
            }
          }}
          startIcon={
            <ArrowDownTrayIcon className="w-5 py-1 text-column-grey-400" />
          }
          type="button"
        />
      </td>
    </>
  );
}
